import { StrategyDetailsPageGetDocumentRecordQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import Button from '@/components/basicComponents/button';
import FileUpload from '@/components/basicComponents/fileUpload';
import { IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import ShowText from '@/components/dueDiligence-page/dueDiligenceRecord/showText';
import { isImageExtension } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { convertDocumentRecordFieldAttachmentToFieldEditorAttachment } from '@/shared/ui/fieldList/utils/fieldUtils';
import { getUTCDate } from '@/shared/utils/date';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { updateMarkdownImageUrls } from '@/shared/utils/fieldEditor';
import { themeVar } from '@/styles/theme';
import {
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  MDXEditorMethods,
  tablePlugin,
  thematicBreakPlugin
} from '@mdxeditor/editor';
import { AIFieldType, TenantType } from '@trace-8/trace8-schema';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DownloadPDF from '../../updatesTab/modals/downloadPDF';

const pluralRules = new Intl.PluralRules('en-US', { type: 'cardinal' });
const formattedFieldValue = (field: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number]) => {
  switch (field.documentField.type) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      if (field?.value && field?.commentary) {
        return `${parseFloat(field.value || '0')?.toFixed(2)}% | ${field.commentary}`;
      }
      return (field?.value && `${parseFloat(field.value || '0')?.toFixed(2)}%`) || (field?.commentary && field.commentary);
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      if (field?.value && field?.commentary) {
        return `${getUTCDate(new Date(field.value)).toUpperCase()} | ${field.commentary}`;
      }
      return (field?.value && getUTCDate(new Date(field.value)).toUpperCase()) || (field?.commentary && field.commentary);
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      const years = pluralRules.select(Number(field?.value)) === 'one' ? 'year' : 'years';

      if (field?.value && field?.commentary) {
        return `${field.value} ${years} | ${field.commentary}`;
      }
      return (field?.value && `${field.value} ${years}`) || (field?.commentary && field.commentary);
    default:
      if (field?.value && field?.commentary) {
        return `${field.value} | ${field.commentary}`;
      }
      return (field?.value && field.value) || (field?.commentary && field.commentary);
  }
};

interface DetailsTabSectionBodyProps {
  documentRecordId: string | null;
  data: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'];
  uploadedFiles: IOfferingMaterialsAttachments[] | [];
  type: 'opportunityDetails' | 'modifyStrategy';
  onEditClick?: () => void;
  openForwardDealDocumentsPage?: () => void;
}

export const DetailsTabSectionBody = ({
  documentRecordId,
  data,
  type,
  uploadedFiles,
  onEditClick,
  openForwardDealDocumentsPage
}: DetailsTabSectionBodyProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });

  const longFormContentEditorRef = useRef<MDXEditorMethods | null>(null);

  useEffect(() => {
    if (longFormContentEditorRef?.current) {
      longFormContentEditorRef.current?.setMarkdown(
        updateMarkdownImageUrls(data?.[1]?.value || '', convertDocumentRecordFieldAttachmentToFieldEditorAttachment(data?.[1]?.attachments || []))
      );
    }
  }, [data]);

  const openAttachment = async (url: string) => {
    window.open(url, '_blank');
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'download-pdf' && (
        <DownloadPDF isOpen={modalWindow.isOpen} updateId={documentRecordId} source="detailsTab" onClose={closeModalWindow} />
      )}
      <DescriptionSectionWrapper>
        {user && user.tenant.type === TenantType.PLATFORM_MANAGER && onEditClick && (
          <EditIconWrap>
            <EditButton onClick={onEditClick}>
              <EditIcon fill={themeVar('font.action')} />
              Edit
            </EditButton>
          </EditIconWrap>
        )}
        {!!data && data.slice(1).some((item) => item?.value || item?.commentary) && (
          <>
            {data?.[1]?.value && (
              <DescriptionWrapper>
                <Divider />
                <Description>
                  <ShowText>
                    <CustomMDXEditor
                      ref={longFormContentEditorRef}
                      markdown={updateMarkdownImageUrls(
                        data?.[1]?.value || '',
                        convertDocumentRecordFieldAttachmentToFieldEditorAttachment(data?.[1]?.attachments || [])
                      )}
                      readOnly
                      suppressHtmlProcessing={true}
                      onError={(error) => console.log(error)}
                      plugins={[
                        listsPlugin(),
                        headingsPlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        thematicBreakPlugin(),
                        imagePlugin({
                          disableImageSettingsButton: true,
                          disableImageResize: true
                        })
                      ]}
                    />
                  </ShowText>
                </Description>
              </DescriptionWrapper>
            )}
            <KeyTermsWrapper>
              <Divider />
              <KeyTerms>
                <KeyTermsItemsWrapper>
                  {data?.slice(2)?.map(
                    (field) =>
                      (field?.value || field?.commentary) && (
                        <KeyTermsItem key={field?.id} isMobile={isMobile}>
                          <ItemKey>{field?.documentField?.name}</ItemKey>
                          <ItemValue>{formattedFieldValue(field)}</ItemValue>
                        </KeyTermsItem>
                      )
                  )}
                </KeyTermsItemsWrapper>
              </KeyTerms>
            </KeyTermsWrapper>
            <DownloadButtonWrapper>
              <Button size="md" styleType="outline" onClick={() => openModalWindow('download-pdf')}>
                Download
              </Button>
            </DownloadButtonWrapper>
          </>
        )}
        {uploadedFiles?.length ? (
          <>
            <PreviewWrapper>
              {uploadedFiles.map((item) => (
                <FileUpload
                  key={item.asset.assetKey}
                  type={isImageExtension(item.asset.assetKey) ? 'image' : 'document'}
                  imageSrc={item.asset.url}
                  fileName={item.asset.assetKey.split('/').pop()}
                  openFile={() => openAttachment(item.asset.url)}
                />
              ))}
            </PreviewWrapper>
            {type === 'opportunityDetails' && (
              <ForwardDealDocumentsButtonWrapper>
                <Button onClick={openForwardDealDocumentsPage} styleType="outline" size="md">
                  Forward Deal Documents
                </Button>
              </ForwardDealDocumentsButtonWrapper>
            )}
          </>
        ) : (
          <></>
        )}
      </DescriptionSectionWrapper>
    </>
  );
};

const EditIconWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px 0 20px;
  color: ${themeVar('font.action')};
`;

const EditButton = styled.div`
  display: flex;
  gap: 4px;
  cursor: pointer;
`;

const DescriptionSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  padding: 20px 20px 20px 0;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  gap: 10px;
`;

const CustomMDXEditor = styled(MDXEditor)`
  font-family: Blinker;
  font-size: 16px;
  .mdxeditor-root-contenteditable div {
    padding: 0;
  }
  .mdxeditor-root-contenteditable ul,
  .mdxeditor-root-contenteditable ol {
    padding-left: 26px;
  }
  .mdxeditor-root-contenteditable img {
    max-width: 100%;
    height: auto;
  }
  .mdxeditor-root-contenteditable th,
  .mdxeditor-root-contenteditable td {
    border: 1px solid var(--baseBgActive);
  }
  button[title='Delete image'][type='button'] {
    display: none !important;
  }
`;

const Divider = styled.div`
  min-width: 5px;
  margin-left: -5px;
  background: ${themeVar('menu.active')};
`;

const KeyTermsWrapper = styled.div`
  display: flex;
  padding: 20px 20px 20px 0;
`;

const KeyTerms = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 20px;
  width: 100%;
`;

const KeyTermsItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KeyTermsItem = styled.div<{ isMobile: boolean }>`
  display: grid;
  align-items: center;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr 2fr' : '1fr 3fr')};
  border-radius: 4px;
  background: ${themeVar('layer.base')};
  padding: 10px 20px;
`;

const ItemKey = styled.div`
  color: ${themeVar('font.base')};
  font-family: Blinker;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const ItemValue = styled(ItemKey)`
  font-weight: 600;
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const ForwardDealDocumentsButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;
